import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';


import Tournament from './components/Tournament';
import SwissStage from './components/Swiss/SwissStage';
import SwissSystemPage from './components/Swiss/SwissSystemPage';
import DoubleEliminationBracket from './components/Brackets/DoubleEliminationBracket';
import AllTeamsPage from './components/Team/AllTeamsPage';
import TeamPage from './components/TeamPage/TeamPage'; 
import Header from './components/Common/Header';
import Footer from './components/Common/Footer';
import DraftAuthGate from './components/Draft/DraftAuthGate';
import DraftPage from './components/Draft/DraftPage';
import PriorityListPage from './components/PriorityList/PriorityListPage';
import { AppContainer, MainContent } from './styles';
import SchedulePage from './components/Schedule/SchedulePage';
import AvailabilityPage from './components/Schedule/AvailabilityPage';
import SubstitutesPage from './components/Players/SubstitutePlayersPage';
import AdminPage from './components/Admin/AdminPage';
import AdminAuthGate from './components/Admin/AdminAuthGate';
import RouteChangeTracker from './components/Common/RouteChangeTracker';
import DivisionSync from './components/Common/DivisionSync';
import AllPlayersPage from './components/Players/AllPlayersPage';
import { useAuth } from './components/Common/AuthContext';
import { useGameMatches } from './context/MatchesContext';
import MatchResultPage from './components/MatchResult/MatchResultPage';
import TeamKnockoutPage from './components/TeamPage/TeamKnockoutPage';
import EditTeamPage from './components/TeamPage/EditTeamPage';
import PlayerProfilePage from './components/Players/PlayerProfilePage';
import { getYearFromHash } from './utils';
import PowerRankingsPage from './components/PowerRankings/PowerRankingsPage';
import CastingPage from './components/Casting/CastingPage';
import CatharsisPage from './components/Catharsis/CatharsisPage';

const AppContentInner: React.FC<{ year: string; urlDivision: string | null; matches: any[] }> = ({ year, urlDivision, matches }) => {
  const { currentUser: user } = useAuth();
  const location = useLocation();

  const isCastingPage = location.pathname.startsWith('/cast/');

  if (isCastingPage) {
    return (
      <Routes>
        <Route path="/cast/:matchId" element={<CastingPage />} />
      </Routes>
    );
  }

  return (
    <AppContainer>
      <Header />
      <MainContent>
        <Routes>
          <Route path="/" element={<Tournament />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/availability" element={<AvailabilityPage />} />
          <Route 
            path="/swiss" 
            element={year === '2026' ? <SwissSystemPage /> : <SwissStage />}
          />
          <Route path="/knockout" element={<DoubleEliminationBracket />} />
          <Route path="/teams" element={<AllTeamsPage />} />
          <Route path="/teams/:teamId" element={<TeamPage matches={matches} />} />
          <Route path="/teams/:teamId/edit" element={<EditTeamPage />} />
          <Route path="/teams/:teamId/knockout" element={<TeamKnockoutPage />} />
          <Route path="/draft-access" element={<DraftAuthGate />} />
          <Route path="/draft/:draftId" element={<DraftPage />} />
          <Route path="/draft" element={<DraftPage />} />
          {user && <Route path="/pick-priority" element={<PriorityListPage />} />}
          <Route path="/subs" element={<SubstitutesPage />} />
          <Route path="/admin-access" element={<AdminAuthGate />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/players" element={<AllPlayersPage />} />
          <Route path="/match/:matchId" element={<MatchResultPage />} />
          <Route path="/players/:playerId" element={<PlayerProfilePage />} />
          <Route path="/power-rankings" element={<PowerRankingsPage />} />
          <Route path="/catharsis" element={<CatharsisPage />} />
          <Route path="/showmatch" element={<CatharsisPage />} />
        </Routes>
      </MainContent>
      <Footer />
    </AppContainer>
  );
};

const AppContent: React.FC = () => {
  const { matches } = useGameMatches();
  const [hash, setHash] = useState(window.location.hash);
  
  useEffect(() => {
    const handleHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const year = getYearFromHash(hash) || '2026';
  const basename = getYearFromHash(hash) ? `/${getYearFromHash(hash)}` : undefined;

  // Retrieve division from current URL hash parameters
  const queryParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
  const urlDivision = queryParams.get('division');

  return (
    <Router basename={basename}>
      <RouteChangeTracker />
      <DivisionSync />
      <AppContentInner year={year} urlDivision={urlDivision} matches={matches} />
    </Router>
  );
}

export default AppContent;